<template>
  <iColumn
    :max-width="LEFT_PANEL_MAX_WIDTH"
    :min-width="LEFT_PANEL_TWO_MIN_WIDTH"
    :width="leftPanelWidth"
    class="panel-content"
    height="fill"
    overflow="none"
    resizable-right
    wrap="nowrap"
    @resize:width="resizePanel"
  >
    <iRow
      :min-height="52"
      align="between"
      class="panel-header"
      vertical-align="middle"
      wrap="nowrap"
    >
      <iText
        :wrap="false"
        ellipsis
        font-size="extraLarge"
        font-weight="bold"
      >
        {{ sectionTitle }}
      </iText>
      <iColumn vertical-align="middle" width="hug">
        <iButton
          v-if="showCreateButton"
          size="extraSmall"
          text-display="inline-flex"
          variant="tertiary"
          vertical-align="middle"
          @click="openCreateModal"
        >
          <iIcon icon="plus" />
        </iButton>
      </iColumn>
    </iRow>
    <iDivider color="disabled" />
    <iRow
      class="panel-body-wrapper"
      gap="extraSmall"
      height="fill"
      overflow="auto"
    >
      <transition
        :name="currentTransition"
        appear
        mode="out-in"
        @after-enter="handleTransitionComplete"
      >
        <div
          v-show="activeSection === 'publications'"
          class="panel-body"
        >
          <iColumn>
            <iColumn gap="extraSmall">
              <iText font-weight="dark">
                Active Publications
              </iText>
              <iDivider variant="subtle" />
              <PublicationsConfigPanel
                v-if="activePublications.length > 0"
                :currently-selected-publication-id="currentlySelectedPublicationId"
                :publications="activePublications"
                @select:publication="$emit('select:publication', $event)"
              />
              <iText v-else variant="subtle">
                No active publications. Create a publication to get started.
              </iText>
            </iColumn>
            <iColumn gap="extraSmall">
              <iText font-weight="dark">
                Draft Publications
              </iText>
              <iDivider variant="subtle" />
              <PublicationsConfigPanel
                v-if="draftPublications.length > 0"
                :currently-selected-publication-id="currentlySelectedPublicationId"
                :publications="draftPublications"
                @select:publication="$emit('select:publication', $event)"
              />
              <iText v-else variant="subtle">
                No draft publications. Create a publication to get started.
              </iText>
            </iColumn>
            <iColumn gap="extraSmall">
              <iText font-weight="dark">
                Collaborating Publications
              </iText>
              <iDivider variant="subtle" />
              <PublicationsConfigPanel
                v-if="collaboratingPublications.length > 0"
                :currently-selected-publication-id="currentlySelectedPublicationId"
                :publications="collaboratingPublications"
                @select:publication="$emit('select:publication', $event)"
              />
              <iText v-else variant="subtle">
                No collaborating publications.
              </iText>
            </iColumn>
          </iColumn>
        </div>
      </transition>
      <transition
        :name="currentTransition"
        appear
        mode="out-in"
        @after-enter="handleTransitionComplete"
      >
        <div
          v-show="activeSection === 'apps'"
          class="panel-body"
        >
          <AppsConfigPanel
            v-if="Object.keys(dynamicApps).length > 0"
            :apps="dynamicApps"
            :currently-selected-app-id="currentlySelectedAppId"
            @select:app="$emit('select:app', $event)"
          />
          <iText v-else>
            No apps. Create an app to get started.
          </iText>
        </div>
      </transition>
    </iRow>
  </iColumn>

  <teleport to="body">
    <iModal
      v-if="showCreatePublicationModal"
      :primary-action-button-disabled="!canCreatePublication"
      :primary-action-button-label="'Create'"
      :primary-action-button-loading="isLoadingCreatePublication"
      title="Create a Publication"
      @close="closeCreateModal"
      @click:primary="handleCreatePublication"
    >
      <template #body>
        <iColumn wrap="nowrap">
          <iTextInput
            v-model="createItem.name"
            placeholder="Publication Name"
            width="fill"
          />
          <iTextArea
            v-model="createItem.description"
            placeholder="Publication Description"
            width="fill"
          />

          <iRow>
            <iSubHeading>Access Control</iSubHeading>
          </iRow>
          <iMasonry :key="createItem.selectedSettingKey">
            <PublicationSettingsCard
              v-for="(settings, key) in signUpSettingsMap"
              :key="key"
              :description="settings.description"
              :features="settings.features"
              :focused="createItem.selectedSettingKey === key"
              :title="settings.title"
              @select="createItem.selectedSettingKey = key"
            />
          </iMasonry>

          <iRow>
            <iSubHeading>
              Select a starting template
            </iSubHeading>
          </iRow>
          <SmartFeed
            :pinia-store="usePublicationTemplateFeedStore"
            default-entity-layout="cardSmall"
            default-smart-feed-layout="grid"
          />
        </iColumn>
      </template>
    </iModal>
  </teleport>
  <teleport to="body">
    <iModal
      v-if="showCreateAppModal"
      :primary-action-button-disabled="!canCreateApp"
      :primary-action-button-label="'Create'"
      :primary-action-button-loading="isLoadingCreateApp"
      title="Create an App"
      @close="closeCreateModal"
      @click:primary="handleCreateApp"
    >
      <template #body>
        <iColumn wrap="nowrap">
          <iTextInput
            v-model="createItem.name"
            placeholder="Publication Name"
            width="fill"
          />
          <iTextArea
            v-model="createItem.description"
            placeholder="Publication Description"
            width="fill"
          />
        </iColumn>
      </template>
    </iModal>
  </teleport>
</template>

<script>
import {
  LEFT_PANEL_MAX_WIDTH,
  LEFT_PANEL_TWO_MIN_WIDTH,
} from "~/stores/staging-frame-store";
import { mapActions, mapState } from "pinia";
import PublicationsConfigPanel from "@/components/create-dashboard/PublicationsConfigPanel.vue";
import { useCreateStore } from "@/stores/create-store";
import SmartFeed from "SmartFeed/components/SmartFeed.vue";
import PublicationSettingsCard from "@/components/publication/PublicationLoginSettingsCard.vue";
import { SIGN_UP_SETTINGS_MAP } from "@/constants/sign-up-settings-map";
import { usePublicationTemplateFeedStore } from "SmartFeed/stores/feed/publication-template-feed-store";
import AppsConfigPanel from "@/components/create-dashboard/AppsConfigPanel.vue";

export default {
  name: "CreateLeftPanelTwo",
  components: { AppsConfigPanel, PublicationSettingsCard, SmartFeed, PublicationsConfigPanel },
  props: {
    activeSection: {
      type: String,
      default: "publications",
    },
    currentlySelectedPublicationId: {
      type: Number,
      required: false,
      default: 0,
    },
    currentlySelectedAppId: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  emits: ["select:publication", "select:app"],
  data() {
    return {
      LEFT_PANEL_TWO_MIN_WIDTH,
      LEFT_PANEL_MAX_WIDTH,
      didInitialFade: false,
      showCreatePublicationModal: false,
      showCreateAppModal: false,
      leftPanelWidth: LEFT_PANEL_TWO_MIN_WIDTH,
      createItem: {
        name: "",
        description: "",
        selectedSettingKey: "accountNotRequired",
      },
    };
  },
  computed: {
    ...mapState(useCreateStore, [
      "activePublications",
      "draftPublications",
      "collaboratingPublications",
      "dynamicApps",
      "isLoadingCreatePublication",
      "isLoadingCreateApp",
    ]),
    ...mapState(usePublicationTemplateFeedStore, ["selectedTemplate"]),
    showCreateButton() {
      if (this.activeSection === "publications") {
        return true;
      } else if (this.activeSection === "apps") {
        return true;
      }
      return false;
    },
    canCreatePublication() {
      return this.createItem.name?.trim() &&
          this.createItem.description?.trim() &&
          this.selectedTemplate !== null &&
          this.selectedTemplate !== undefined;
    },
    canCreateApp() {
      return this.createItem.name?.trim() &&
          this.createItem.description?.trim();
    },
    signUpSettingsMap() {
      return SIGN_UP_SETTINGS_MAP;
    },
    sectionTitle() {
      if (this.activeSection === "publications") {
        return "Publications";
      } else if (this.activeSection === "apps") {
        return "Apps";
      } else {
        return "";
      }
    },
    currentTransition() {
      return "panel-fade";
    },
  },
  methods: {
    ...mapActions(useCreateStore, [
      "createPublication",
      "createApp",
    ]),
    usePublicationTemplateFeedStore,
    handleTransitionComplete() {
      this.didInitialFade = true;
    },
    resizePanel(width) {
      if (width > LEFT_PANEL_MAX_WIDTH) {
        width = LEFT_PANEL_MAX_WIDTH;
      }

      if (width < LEFT_PANEL_TWO_MIN_WIDTH) {
        width = LEFT_PANEL_TWO_MIN_WIDTH;
      }

      this.leftPanelWidth = width;
    },
    handleCreateApp() {
      this.createApp(
        this.createItem.name,
        this.createItem.description,
        data => {
          useCreateStore().dynamicApps[data.dynamic_app_id] = data;
          this.$emit("select:app", data);
          this.closeCreateModal();
        },
      );
    },
    handleCreatePublication() {
      const accessControlFeatures = this.signUpSettingsMap[this.createItem.selectedSettingKey].features;
      this.createPublication(
        this.createItem.name,
        this.createItem.description,
        this.selectedTemplate,
        "0",
        accessControlFeatures,
        data => {
          useCreateStore().publications.push(data.publicationData);
          this.$emit("select:publication", data.publicationData);
          this.closeCreateModal();
        },
      );
    },
    openCreateModal() {
      if (this.activeSection === "publications") {
        this.showCreatePublicationModal = true;
      } else if (this.activeSection === "apps") {
        this.showCreateAppModal = true;
      }
    },
    closeCreateModal() {
      this.showCreatePublicationModal = false;
      this.showCreateAppModal = false;
      this.createItem = {
        name: "",
        description: "",
        selectedSettingKey: "accountNotRequired",
      };
    },
  },
  styleGuide: () => ({
    "paddingStandard": { "size.spacing": "standard" },
    "backgroundColor": { "color.background": "paper" },
    "rightBorderColor": { "color.border": "dark" },
  }),
};
</script>

<style lang="scss" scoped>
.panel-content {
  padding-inline: v-bind("$getStyles.paddingStandard");
  border-right: 1px solid v-bind("$getStyles.rightBorderColor");
  height: calc(100vh - 67px) !important;
  position: relative;
}

.panel-header {
  padding-top: v-bind("$getStyles.paddingStandard");
  z-index: 1;
  background-color: v-bind("$getStyles.backgroundColor");

  &.sticky {
    position: sticky;
    top: 0;
  }
}

.clickable {
  cursor: pointer;
}

:deep(.i-tabs__content.col) {
  height: v-bind(tabContentHeight) !important;
}

.panel-body-wrapper {
  position: relative;
  overflow: hidden;
}

.panel-body {
  position: absolute;
  width: 100%;
  height: 100%;
}

// Slide from right to left
.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 0.3s ease;
}

.slide-left-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.slide-left-enter-to {
  transform: translateX(0%);
  opacity: 1;
}

.slide-left-leave-from {
  transform: translateX(0%);
  opacity: 1;
}

.slide-left-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

// Slide from left to right
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.3s ease;
}

.slide-right-enter-from {
  transform: translateX(-100%);
  opacity: 0;
}

.slide-right-enter-to {
  transform: translateX(0%);
  opacity: 1;
}

.slide-right-leave-from {
  transform: translateX(0%);
  opacity: 1;
}

.slide-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.panel-fade-enter-active,
.panel-fade-leave-active {
  transition: opacity 0.15s ease-in-out;
}

.panel-fade-enter-from,
.panel-fade-leave-to {
  opacity: 0;
}
</style>
